<script setup lang="ts">
/**
 * Правая докпанель ассистента (макет 8-1, 8-2a).
 *
 * Панель — сосед `<main>` во flex-каркасе, а не оверлей поверх него: контент
 * физически теряет ширину и переверстывается сам. Оверлей закрывал бы ровно ту
 * задачу, о которой спрашивают.
 *
 * Ни разговора, ни соединения здесь нет — всё в сторе. Панель размонтируется
 * при закрытии, и ход, привязанный к её жизненному циклу, обрывался бы вместе
 * с ней.
 */
import { History, Plus, Sparkle, X } from 'lucide-vue-next'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { listThreads, type ThreadBrief } from '@/features/assistant/api'
import { MAX_WIDTH, MIN_WIDTH, snapWidth, useAssistantStore } from '@/stores/assistant'

const { t } = useI18n()
const assistant = useAssistantStore()

const draft = ref('')
const body = ref<HTMLElement | null>(null)
const isResizing = ref(false)
const isHistoryOpen = ref(false)
const threads = ref<ThreadBrief[]>([])

const suggestions = computed(() => [
  t('assistant.suggestions.board'),
  t('assistant.suggestions.overdue'),
  t('assistant.suggestions.compose'),
])

const quotaNotice = computed(() => {
  const quota = assistant.quota

  // Лимит ноль означает «без лимита»: показывать «осталось 0 из 0» незачем.
  if (!quota || !quota.limit) return ''

  return t('assistant.quota', { remaining: quota.remaining, limit: quota.limit })
})

/** Ответ дописывается снизу — окно прокрутки едет за ним. */
watch(
  () => [assistant.messages.length, assistant.messages[assistant.messages.length - 1]?.text],
  async () => {
    await nextTick()

    if (body.value) body.value.scrollTop = body.value.scrollHeight
  },
)

const submit = () => {
  const text = draft.value

  draft.value = ''

  void assistant.send(text)
}

const ask = (text: string) => {
  draft.value = text
  submit()
}

const toggleHistory = async () => {
  isHistoryOpen.value = !isHistoryOpen.value

  if (!isHistoryOpen.value) return

  try {
    threads.value = await listThreads(assistant.context)
  } catch {
    // Переключатель — вспомогательный путь: не открылся список, разговор
    // продолжается в текущем треде.
    threads.value = []
  }
}

const pick = async (id: string) => {
  isHistoryOpen.value = false

  await assistant.openThread(id)
}

/**
 * Перетаскивание кромки.
 *
 * Слушатели вешаются на окно, а не на саму кромку: курсор обгоняет её на
 * быстром движении, и события ушли бы соседнему элементу вместе с драгом.
 * Ширина считается от точки захвата, а не от текущей позиции курсора — иначе
 * панель прыгает на пол-кромки в первый же кадр.
 */
const startResize = (event: PointerEvent) => {
  event.preventDefault()

  const startX = event.clientX
  const startWidth = assistant.width

  isResizing.value = true

  const onMove = (move: PointerEvent) => {
    assistant.setWidth(snapWidth(startWidth + (startX - move.clientX)))
  }

  const onUp = () => {
    isResizing.value = false
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
  }

  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
}

/** Клавиатурный ресайз: панель шириной в 320 px — это и полоса чтения тоже. */
const nudge = (delta: number) => assistant.setWidth(assistant.width + delta)
</script>

<template>
  <aside
    class="apanel"
    :class="{ 'apanel--resizing': isResizing }"
    :style="{ width: `${assistant.width}px` }"
    :aria-label="t('assistant.title')"
  >
    <div
      class="apanel__grip"
      role="separator"
      tabindex="0"
      aria-orientation="vertical"
      :aria-label="t('assistant.resize')"
      :aria-valuenow="assistant.width"
      :aria-valuemin="MIN_WIDTH"
      :aria-valuemax="MAX_WIDTH"
      @pointerdown="startResize"
      @keydown.left.prevent="nudge(20)"
      @keydown.right.prevent="nudge(-20)"
    />

    <header class="apanel__head">
      <Sparkle class="apanel__spark" :size="16" />
      <span class="apanel__title">{{ t('assistant.title') }}</span>

      <button
        type="button"
        class="apanel__icon"
        :title="t('assistant.history')"
        :aria-label="t('assistant.history')"
        :aria-expanded="isHistoryOpen"
        @click="toggleHistory"
      >
        <History :size="16" />
      </button>

      <button
        type="button"
        class="apanel__icon"
        :title="t('assistant.newThread')"
        :aria-label="t('assistant.newThread')"
        @click="assistant.reset()"
      >
        <Plus :size="16" />
      </button>

      <button
        type="button"
        class="apanel__icon"
        :title="t('assistant.close')"
        :aria-label="t('assistant.close')"
        @click="assistant.close()"
      >
        <X :size="16" />
      </button>
    </header>

    <ul v-if="isHistoryOpen" class="apanel__threads">
      <li v-for="thread in threads" :key="thread.id">
        <button type="button" class="apanel__thread" @click="pick(thread.id)">
          {{ thread.title || t('assistant.untitled') }}
        </button>
      </li>

      <li v-if="!threads.length" class="apanel__threads-empty">{{ t('assistant.noThreads') }}</li>
    </ul>

    <div class="apanel__context">
      <span v-if="assistant.context.label" class="apanel__chip">{{ assistant.context.label }}</span>

      <button type="button" class="apanel__new" @click="assistant.reset()">
        {{ t('assistant.newThread') }}
      </button>
    </div>

    <div ref="body" class="apanel__body">
      <template v-if="assistant.isEmpty">
        <p class="apanel__disclaimer">{{ t('assistant.disclaimer') }}</p>

        <button
          v-for="suggestion in suggestions"
          :key="suggestion"
          type="button"
          class="apanel__suggestion"
          @click="ask(suggestion)"
        >
          {{ suggestion }}
        </button>
      </template>

      <!--
        Один живой регион на весь разговор, а не по региону на реплику: ответ
        приезжает кусками, и экранный диктор должен читать его по мере дописи,
        а не начинать заново на каждое сообщение.
      -->
      <div class="apanel__stream" aria-live="polite" aria-atomic="false">
        <div v-for="message in assistant.messages" :key="message.id" class="apanel__message">
          <span
            class="apanel__author"
            :class="{ 'apanel__author--ai': message.role === 'assistant' }"
          >
            {{ message.role === 'assistant' ? t('assistant.title') : t('assistant.you') }}
          </span>
          <p class="apanel__text">{{ message.text }}</p>
        </div>
      </div>

      <!--
        Полоска ожидания живёт ровно до первого слова ответа: между вопросом и
        текстом модель ходит по инструментам, и без неё экран выглядит мёртвым.
      -->
      <div v-if="assistant.isStreaming && !assistant.hasText" class="apanel__progress">
        <span class="apanel__progress-line" />
        <span class="apanel__waiting">{{ t('assistant.waiting') }}</span>
      </div>

      <p v-if="assistant.error" class="apanel__error" role="alert">{{ assistant.error }}</p>
    </div>

    <form class="apanel__composer" @submit.prevent="submit">
      <div class="apanel__input">
        <input
          v-model="draft"
          class="apanel__field"
          :placeholder="t('assistant.placeholder')"
          :aria-label="t('assistant.placeholder')"
          :disabled="assistant.isStreaming"
        />

        <button
          v-if="assistant.isStreaming"
          type="button"
          class="apanel__stop"
          @click="assistant.stop()"
        >
          {{ t('assistant.stop') }}
        </button>

        <span v-else class="apanel__kbd">⌘K</span>
      </div>

      <p v-if="quotaNotice" class="apanel__quota">{{ quotaNotice }}</p>
    </form>
  </aside>
</template>

<style scoped>
.apanel {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  border-left: 1px solid var(--color-border);
  background: var(--color-surface);
  transition: width 120ms ease;
}

/* Во время драга анимация ширины отстаёт от курсора и читается как рывки. */
.apanel--resizing {
  transition: none;
  user-select: none;
}

.apanel__grip {
  position: absolute;
  top: 0;
  left: -3px;
  z-index: 1;
  width: 6px;
  height: 100%;
  cursor: col-resize;
}

.apanel__grip:hover,
.apanel__grip:focus-visible {
  background: var(--color-primary);
  opacity: 0.3;
}

.apanel__head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 48px;
  padding: 0 16px;
}

.apanel__spark {
  color: var(--color-primary);
}

.apanel__title {
  flex: 1;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
}

.apanel__icon {
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.apanel__icon:hover {
  color: var(--color-text-primary);
}

.apanel__threads {
  margin: 0 16px;
  padding: 4px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
  list-style: none;
}

.apanel__thread {
  display: block;
  width: 100%;
  padding: 6px 8px;
  overflow: hidden;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  text-align: left;
  text-overflow: ellipsis;
  cursor: pointer;
}

.apanel__thread:hover {
  background: var(--color-bg-muted);
}

.apanel__threads-empty {
  padding: 6px 8px;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.apanel__context {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  height: 32px;
  padding: 0 16px;
}

.apanel__chip {
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
}

.apanel__new {
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-caption);
  cursor: pointer;
}

.apanel__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  border-top: 1px solid var(--color-border);
}

.apanel__disclaimer {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.apanel__suggestion {
  padding: 7px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.apanel__suggestion:hover {
  background: var(--color-bg-muted);
}

.apanel__stream {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.apanel__message {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.apanel__author {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.apanel__author--ai {
  color: var(--color-primary);
}

/* Реплики без пузырей: панель узкая, и рамка вокруг текста съедает её вдвое. */
.apanel__text {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.apanel__progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.apanel__progress-line {
  height: 2px;
  overflow: hidden;
  border-radius: var(--radius-full);
  background: var(--color-bg-muted);
}

.apanel__progress-line::after {
  display: block;
  width: 40%;
  height: 100%;
  border-radius: var(--radius-full);
  background: var(--color-primary);
  animation: apanel-slide 1.2s ease-in-out infinite;
  content: '';
}

@keyframes apanel-slide {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(250%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .apanel__progress-line::after {
    animation: none;
  }
}

.apanel__waiting {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.apanel__error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-caption);
}

.apanel__composer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
  padding: 12px;
  border-top: 1px solid var(--color-border);
}

.apanel__input {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-app);
}

.apanel__field {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
}

.apanel__field:focus {
  outline: none;
}

.apanel__kbd {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
}

.apanel__stop {
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-caption);
  cursor: pointer;
}

.apanel__quota {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}
</style>
